import React from "react";
import { NextPageContext } from "next";
import qs from "query-string";

import { getQueryValue } from "@lib/getQueryValue";
import { styled } from "@styles/theme";
import Logo from "@icons/illustrations/logo";

import { Spacer } from "@components/common/Spacer";
import { Row, Col } from "@components/common/Grid";
import { Flex } from "@components/common/ResponsiveBox";
import { Text } from "@components/common/Text";
import Link from "@components/common/Link";
import { BackButton } from "@components/common/BackBtn";
import { SignUpForm as SignUpFormComponent } from "@components/pages/account/SignUpForm";

const Container = styled.div`
  width: 100%;
  overflow: hidden;
  min-height: 100vh;
  background-color: #ffffff;
  padding-bottom: ${(p) => p.theme.spacing.xxxg};
`;

const Heading = styled.h1`
  font-size: 24px;
  text-align: center;
  font-weight: bold;
  color: ${(p) => p.theme.colors.primary[500]};
`;

type Props = { source: string; plan: string };
type SignUpProps = {
  fromLandingPage: string;
  accentureLoginUrl?: string;
  termsLink?: string;
  privacyLink?: string;
};

export const SignUpForm = ({
  fromLandingPage,
  accentureLoginUrl = "/login/sso",
  termsLink = "/terms-of-service",
}: SignUpProps) => {
  return (
    <>
      <BackButton href={fromLandingPage}>Back</BackButton>
      <Spacer size="xg" />
      <Heading>Create your account</Heading>
      <Spacer size="xs" />
      <Text paragraph align="center" colorWeight="200">
        Already have an account? <Link href={fromLandingPage}>Log in.</Link>
      </Text>
      <Spacer size="xg" />
      <SignUpFormComponent accentureLoginUrl={accentureLoginUrl} />
      <Spacer size="xs" />
      <Text size={12} colorWeight="200">
        All fields on this form are mandatory
      </Text>
      <Spacer size="xg" />
      <Text size={12} align="center" colorWeight="200">
        Creating your account means you accept our{" "}
        <Link small a href={termsLink}>
          terms of service
        </Link>
      </Text>
    </>
  );
};

function SignUp({ source, plan }: Props) {
  const fromLandingPage = `/login?${
    source && plan ? qs.stringify({ source, plan }) : ""
  }`;

  return (
    <Container>
      <Flex
        mT={{ 0: "md", 2: "xxxg" }}
        mX="auto"
        maxW={1200}
        pX={{ 0: "md", 3: "none" }}
      >
        <Flex mT="xxs">
          <Logo fill="dark" />
        </Flex>
        <Row>
          <Col mX="auto" mT="sm" size={{ 0: 12, 2: 5 }}>
            <SignUpForm fromLandingPage={fromLandingPage} />
          </Col>
        </Row>
      </Flex>
    </Container>
  );
}

SignUp.getInitialProps = (ctx: NextPageContext): Props => {
  const q = ctx.query;
  const plan = getQueryValue(q.plan);
  const source = getQueryValue(q.source);
  return { source, plan };
};

SignUp.public = true;

export default SignUp;
